import React, { useCallback, useContext, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import type { Episode, Series } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { Col, ImagePreset, LicenseType, usePlayable, useProgress } from '@vodafoneis/sjonvarpskjarni-js-lib';
import type { IPlayerProps, Player as LottiePlayerInstance } from '@lottiefiles/react-lottie-player';
import { ImageComponent, ImageComponentContainer } from './ImageComponent';
import { FONT_SECONDARY, PROGRESS_BACKGROUND, PROGRESS_FOREGROUND } from '../styles/colors';
import playIcon from '../../public/images/play-icon.json';
import { useAvailableUntil } from '../hooks/useAvailableUntil';
import { SeriesSceneContext } from '../contexts/SeriesSceneContext';
import { ExpandableText } from './ExpandableText';
import { UserContext } from '../contexts/UserContext';

type LottiePlayerProps = IPlayerProps & { playerRef: React.Ref<LottiePlayerInstance> };

const LottiePlayer = dynamic(
	() =>
		import('@lottiefiles/react-lottie-player').then(({ Player }) => {
			const LottiePlayerWithRef: React.FC<LottiePlayerProps> = ({ playerRef, ...props }) => <Player ref={playerRef} {...props} />;
			return LottiePlayerWithRef;
		}),
	{ ssr: false }
);

const EpisodeContainer = styled.div`
	display: flex;
	margin-bottom: 10px;
`;

const EpisodeImageContainer = styled(ImageComponentContainer)`
	width: 33%;
	min-width: 33%;
	max-width: 33%;
	aspect-ratio: 16/9;
	border-radius: 6px;
	overflow: hidden;
	margin-right: 20px;
	position: relative;
	display: flex;
	justify-content: center;
	flex-direction: column;
	cursor: pointer;
`;

const EpisodeImage = styled(ImageComponent)`
	width: 100%;
	height: 100%;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
`;

const EpisodeTitleContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding-right: 20px;
`;

const EpisodeTitle = styled.div`
	font-weight: bold;
	line-height: 1.3;
	font-size: 20px;
	text-overflow: ellipsis;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	white-space: normal;
`;

const EpisodeSubtitle = styled.div`
	color: ${FONT_SECONDARY};
	line-height: 1.3;
	font-size: 18px;
`;

const EpisodeDescription = styled.div`
	line-height: 1.3;
	margin-bottom: 30px;
	font-size: 18px;
	padding-right: 20px;
`;

const EpisodeProgressContainer = styled.div`
	position: absolute;
	left: 0;
	bottom: 0;
	right: 0;
	background: ${PROGRESS_BACKGROUND};
`;

type EpisodeProgressProps = {
	progress: number;
};

const EpisodeProgress = styled.div<EpisodeProgressProps>`
	width: ${(props) => Math.round(props.progress * 100)}%;
	height: 4px;
	background: ${PROGRESS_FOREGROUND};
`;

export const EpisodeComponent: React.FC<{ series: Series; episode: Episode }> = ({ series, episode }) => {
	const { t } = useTranslation();
	const { isLoggedIn } = useContext(UserContext);
	const { playEpisode } = useContext(SeriesSceneContext);

	const playIconEl = useRef<LottiePlayerInstance>(null);

	const { isWatchable } = usePlayable(episode);
	const availableUntil = useAvailableUntil(episode);
	const { progress, position, hasProgress } = useProgress(episode);

	const onClickHandler = useCallback(
		(e) => {
			e.preventDefault();
			playIconEl.current?.play();
			if (isLoggedIn) {
				if (hasProgress) {
					playEpisode(episode, position);
				} else {
					playEpisode(episode);
				}
			}
		},
		[episode, hasProgress, isLoggedIn, playEpisode, position]
	);

	const subtitle = useMemo(() => {
		if (episode.isTVOD()) {
			const license = episode.getLicense(LicenseType.TVOD);

			if (license?.rental?.validUntil) {
				return t('ContentInfo.RentedUntil.WithDate', {
					date: moment(license?.rental?.validUntil).format('D. MMM'),
				});
			}

			return t('ContentInfo.Price', {
				price: license.price.value,
			});
		}

		return availableUntil ? `${t('ContentInfo.AvailableUntil.Title')} ${availableUntil}` : `${episode.duration} mín.`;
	}, [availableUntil, episode, t]);

	const playIconComponent = useMemo(() => {
		if (!isLoggedIn || !isWatchable) return null;

		return <LottiePlayer playerRef={playIconEl} src={playIcon} style={{ width: '50%' }} speed={10} />;
	}, [isLoggedIn, isWatchable]);

	return (
		<Col xs={12} md={6} lg={4}>
			<EpisodeContainer>
				<EpisodeImageContainer onClick={onClickHandler}>
					<EpisodeImage images={[episode.getStillImageUrl({ presets: [ImagePreset.STILL_MEDIUM] }), episode.getBackdropImageUrl({ presets: [ImagePreset.STILL_MEDIUM] })]} />
					{playIconComponent}
					<EpisodeProgressContainer>
						<EpisodeProgress progress={progress} />
					</EpisodeProgressContainer>
				</EpisodeImageContainer>
				<EpisodeTitleContainer>
					<EpisodeTitle>{`${episode.episodeNumber}. ${episode.title}`}</EpisodeTitle>
					<EpisodeSubtitle>{subtitle}</EpisodeSubtitle>
				</EpisodeTitleContainer>
			</EpisodeContainer>
			<EpisodeDescription>
				<ExpandableText numberOfLines={3}>{episode.description && episode.description !== series.description && episode.description}</ExpandableText>
			</EpisodeDescription>
		</Col>
	);
};
